// frontend/src/features/aw/AWAOAModal.jsx
import React, { useEffect, useState } from "react";
import { getClients, getAccountDetails, getFormDefaults } from "./api";
import api from "../../apiClient";

// Fix "Last,First" → "First Last"
function fixName(name) {
  if (!name) return "";
  const s = name.trim();
  // Match "LastName,FirstName" or "LastName, FirstName"
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) return `${m[2].trim()} ${m[1].trim()}`;
  return s;
}

export default function AWAOAModal({ accountIds, accounts, clientMap, onClose }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [contactId, setContactId] = useState("");
  const [markPending, setMarkPending] = useState(true);
  const [allProperty, setAllProperty] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [cadDetails, setCadDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasValidSig, setHasValidSig] = useState(false);
  const [sigFile, setSigFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState({
    appraisal_district: "",
    owner_name: "", owner_phone: "", owner_address: "", owner_city_state_zip: "",
    property_address: "", legal_description: "",
    agent_name: "", agent_phone: "", agent_address: "", agent_city_state_zip: "",
    expiration_date: "",
    signer_name: "", signer_title: "", signer_date: new Date().toLocaleDateString("en-US"),
    signer_is_owner: false, signer_is_manager: true,
  });

  useEffect(() => {
    getClients().then(cls => {
      setClients(cls);
      // Auto-select client if dashboard has a match
      const acct = accounts?.[0];
      if (acct?.company_name && clientMap) {
        const match = clientMap[(acct.company_name || "").toLowerCase()];
        if (match?.client?.id) {
          setClientId(String(match.client.id));
        }
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Fetch CAD details
  useEffect(() => {
    if (!accountIds?.length) return;
    setLoadingDetails(true);
    getAccountDetails(accountIds[0]).then(d => {
      if (d.ok) {
        setCadDetails(d);
        setForm(f => ({
          ...f,
          owner_name: d.owner_name || f.owner_name,
          owner_address: d.owner_address || f.owner_address,
          owner_city_state_zip: d.owner_city_state_zip || f.owner_city_state_zip,
          property_address: d.property_address || f.property_address,
          legal_description: d.legal_description || f.legal_description,
          appraisal_district: d.appraisal_district || f.appraisal_district,
        }));
      }
    }).catch(() => {}).finally(() => setLoadingDetails(false));
  }, [accountIds]); // eslint-disable-line

  // Load form defaults from DB on mount
  useEffect(() => {
    getFormDefaults().then(defaults => {
      setForm(f => ({
        ...f,
        agent_name: defaults.agent_name || "BA Property Tax",
        agent_phone: defaults.agent_phone || "",
        agent_address: defaults.agent_address || "",
        agent_city_state_zip: defaults.agent_city_state_zip || "",
      }));
    }).catch(() => {});
  }, []);

  const selectedClient = clients.find(c => c.id === parseInt(clientId));
  const contacts = selectedClient?.contacts || [];

  const handleClientChange = (id) => { setClientId(id); setContactId(""); };
  const handleContactChange = (id) => {
    setContactId(id);
    if (id) {
      const ct = contacts.find(c => c.id === parseInt(id));
      if (ct) setForm(f => ({ ...f, signer_name: fixName(ct.full_name) || "", signer_title: ct.title || "" }));
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const acct = accounts?.[0];
      const selectedContact = contactId ? contacts.find(c => c.id === parseInt(contactId)) : null;
      const ownerPhone = selectedContact?.phone || selectedContact?.mobile || form.owner_phone || "";

      const payload = {
        account_ids: accountIds,
        client_id: clientId ? parseInt(clientId) : null,
        mark_as_pending: markPending,
        appraisal_district: form.appraisal_district,
        owner_name: form.owner_name,
        owner_phone: ownerPhone,
        owner_address: form.owner_address,
        owner_city_state_zip: form.owner_city_state_zip,
        all_property: allProperty,
        property_address: form.property_address,
        legal_description: form.legal_description,
        agent_name: form.agent_name,
        agent_phone: form.agent_phone,
        agent_address: form.agent_address,
        agent_city_state_zip: form.agent_city_state_zip,
        all_tax_matters: true,
        confidential_info: true,
        all_chief_appraiser: true,
        all_arb: true,
        all_taxing_units: true,
        expiration_date: form.expiration_date,
        signer_name: form.signer_name,
        signer_title: form.signer_title,
        signer_date: form.signer_date,
        signer_is_owner: form.signer_is_owner,
        signer_is_manager: form.signer_is_manager,
      };

      let res;
      if (hasValidSig && sigFile) {
        const fd = new FormData();
        fd.append("payload", JSON.stringify(payload));
        fd.append("signature_pdf", sigFile);
        res = await api.post("/aw/aoa/generate", fd, {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("/aw/aoa/generate", payload, { responseType: "blob" });
      }

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AOA_${acct?.parcel_acct_num || "accounts"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose?.();
    } catch (e) {
      alert("Failed to generate AOA: " + (e.message || "Unknown error"));
    }
    setLoading(false);
  };

  const acct = accounts?.[0];

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: "90vh" }}>
        <div className="aw-modal-hdr">
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Appointment of Agent (Form 50-162)</h3>
            <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>
              {acct?.parcel_acct_num} &middot; {(acct?.assessor_key || "").toUpperCase()}
            </div>
          </div>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>

        <div className="aw-modal-body">
          {/* Loading indicator */}
          {loadingDetails && (
            <Banner color="59,130,246" icon>Fetching property details from CAD site...</Banner>
          )}
          {cadDetails?.ok && !loadingDetails && (
            <Banner color="34,197,94">Auto-populated from {cadDetails.appraisal_district}</Banner>
          )}

          {/* ── Appraisal District (top of form) ── */}
          <Field label="Appraisal District Name" value={form.appraisal_district} onChange={v => set("appraisal_district", v)} />

          {/* ── STEP 1: Owner ── */}
          <Step n="1" title="Owner's Name and Address" sub="From the CAD property record" />
          <Field label="Name" value={form.owner_name} onChange={v => set("owner_name", v)} />
          <Field label="Telephone" value={form.owner_phone} onChange={v => set("owner_phone", v)}
            placeholder="Auto-filled from contact if available" />
          <Field label="Address" value={form.owner_address} onChange={v => set("owner_address", v)} />
          <Field label="City, State, Zip Code" value={form.owner_city_state_zip} onChange={v => set("owner_city_state_zip", v)} />

          {/* ── STEP 2: Property ── */}
          <Step n="2" title="Property Identification" />
          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            <label style={S.radio}>
              <input type="radio" checked={allProperty} onChange={() => setAllProperty(true)} style={{ accentColor: "var(--accent)" }} />
              All property listed for me at the above address
            </label>
            <label style={S.radio}>
              <input type="radio" checked={!allProperty} onChange={() => setAllProperty(false)} style={{ accentColor: "var(--accent)" }} />
              The property(ies) listed below
            </label>
          </div>
          {!allProperty && (
            <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,.02)", border: "1px solid var(--border-l)" }}>
              <div style={{ display: "flex", gap: 12 }}>
                <Field label="Account Number" value={acct?.parcel_acct_num || ""} onChange={() => {}} disabled />
                <Field label="Situs Address" value={form.property_address} onChange={v => set("property_address", v)} />
              </div>
              <Field label="Legal Description" value={form.legal_description} onChange={v => set("legal_description", v)} />
            </div>
          )}

          {/* ── STEP 3: Agent ── */}
          <Step n="3" title="Identify the Agent" />
          <Field label="Name (on form)" value={form.agent_name} onChange={v => set("agent_name", v)} />
          <Field label="Telephone" value={form.agent_phone} onChange={v => set("agent_phone", v)} />
          <Field label="Address" value={form.agent_address} onChange={v => set("agent_address", v)} />
          <Field label="City, State, Zip Code" value={form.agent_city_state_zip} onChange={v => set("agent_city_state_zip", v)} />

          {/* ── STEP 4: Authority (auto-checked, shown as info) ── */}
          <Step n="4" title="Agent's Authority" />
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(34,197,94,.05)", border: "1px solid rgba(34,197,94,.12)", fontSize: 11, color: "var(--muted)" }}>
            <div style={{ marginBottom: 4 }}><Check /> All property tax matters concerning the property identified</div>
            <div style={{ marginBottom: 4 }}><Check /> Authorized to receive confidential information &mdash; <span style={{ color: "#50d87a" }}>Yes</span></div>
            <div style={{ marginBottom: 4 }}><Check /> All communications from the chief appraiser</div>
            <div style={{ marginBottom: 4 }}><Check /> All communications from the appraisal review board</div>
            <div><Check /> All communications from all taxing units</div>
          </div>

          {/* ── STEP 5: Expiration ── */}
          <Step n="5" title="Date the Agent's Authority Ends" />
          <Field label="Expiration Date" value={form.expiration_date} onChange={v => set("expiration_date", v)}
            placeholder="Leave blank for no expiration" />

          {/* ── STEP 6: Signer ── */}
          <Step n="6" title="Identification, Signature, and Date" sub="Select client & contact to auto-fill" />

          <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="aw-label">Client (Company)</label>
              <select className="aw-sel" style={{ width: "100%" }} value={clientId} onChange={e => handleClientChange(e.target.value)}>
                <option value="">-- Select or enter manually --</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {clientId && contacts.length > 0 && (
              <div style={{ flex: 1 }}>
                <label className="aw-label">Contact</label>
                <select className="aw-sel" style={{ width: "100%" }} value={contactId} onChange={e => handleContactChange(e.target.value)}>
                  <option value="">-- Select --</option>
                  {contacts.map(ct => (
                    <option key={ct.id} value={ct.id}>
                      {fixName(ct.full_name)}{ct.title ? ` (${ct.title})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <Field label="Printed Name" value={form.signer_name} onChange={v => set("signer_name", v)} />
            <Field label="Title" value={form.signer_title} onChange={v => set("signer_title", v)} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Field label="Date" value={form.signer_date} onChange={v => set("signer_date", v)} />
            <div style={{ flex: 1 }}>
              <label className="aw-label">Signer Is</label>
              <div style={{ display: "flex", gap: 12, paddingTop: 6 }}>
                {[["signer_is_owner", "Owner"], ["signer_is_manager", "Manager"]].map(([k, l]) => (
                  <label key={k} style={S.radio}>
                    <input type="radio" name="signer_type" checked={form[k]}
                      onChange={() => setForm(f => ({ ...f, signer_is_owner: false, signer_is_manager: false, [k]: true }))}
                      style={{ accentColor: "var(--accent)" }} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ── Valid Signature Upload ── */}
          <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 10, background: "rgba(139,92,246,.06)", border: "1px solid rgba(139,92,246,.15)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#a78bfa" }}>
              <input type="checkbox" checked={hasValidSig} onChange={e => { setHasValidSig(e.target.checked); if (!e.target.checked) setSigFile(null); }} style={{ accentColor: "#8b5cf6" }} />
              Have valid signature
            </label>
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 3, marginLeft: 22 }}>
              Replace the signature page with one from an existing signed AOA.
            </div>
            {hasValidSig && (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f?.type === "application/pdf") setSigFile(f); }}
                onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".pdf"; inp.onchange = e => { if (e.target.files[0]) setSigFile(e.target.files[0]); }; inp.click(); }}
                style={{
                  marginTop: 8, padding: "16px 12px", borderRadius: 8, textAlign: "center", cursor: "pointer",
                  border: `2px dashed ${dragOver ? "#a78bfa" : "rgba(139,92,246,.25)"}`,
                  background: dragOver ? "rgba(139,92,246,.1)" : "rgba(139,92,246,.03)",
                  transition: "all .15s",
                }}
              >
                {sigFile ? (
                  <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 600 }}>
                    {sigFile.name}
                    <span style={{ marginLeft: 8, color: "var(--dim)", fontWeight: 400, cursor: "pointer" }}
                      onClick={e => { e.stopPropagation(); setSigFile(null); }}>&#10005; remove</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: "var(--dim)" }}>
                    Drag &amp; drop a signed AOA PDF here, or click to browse.
                    <div style={{ fontSize: 10, marginTop: 4, color: "var(--dim)", opacity: 0.7 }}>
                      Full AOA, signature page only, or signed PDF with audit trail.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Mark as pending ── */}
          <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 10, background: "rgba(245,158,11,.06)", border: "1px solid rgba(245,158,11,.15)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#e8b040" }}>
              <input type="checkbox" checked={markPending} onChange={e => setMarkPending(e.target.checked)} style={{ accentColor: "#f59e0b" }} />
              Mark as Pending after download
            </label>
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 3, marginLeft: 22 }}>
              Won't inflate the notification badge until re-checked.
            </div>
          </div>
        </div>

        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn-primary" onClick={handleGenerate} disabled={loading || loadingDetails}>
            {loading ? "Generating..." : "Download AOA PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, sub }) {
  return (
    <div style={{ borderTop: "1px solid var(--border-l)", margin: "16px 0 10px", paddingTop: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 6, fontSize: 11, fontWeight: 800,
        background: "var(--accent-g)", color: "var(--accent)", border: "1px solid rgba(139,35,50,.25)",
        flexShrink: 0,
      }}>{n}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{title}</span>
      {sub && <span style={{ fontSize: 10, color: "var(--dim)", fontStyle: "italic" }}>{sub}</span>}
    </div>
  );
}

function Check() {
  return <span style={{ color: "#50d87a", marginRight: 4 }}>&#10003;</span>;
}

function Banner({ children, color, icon }) {
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8, fontSize: 11, marginBottom: 12,
      background: `rgba(${color},.06)`, border: `1px solid rgba(${color},.15)`,
      color: `rgba(${color.split(",").map(c => Math.min(255, parseInt(c) + 80)).join(",")},1)`,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      {icon && <span style={{ display: "inline-block", width: 12, height: 12, border: `2px solid rgba(${color},.3)`, borderTopColor: `rgba(${color},.8)`, borderRadius: "50%", animation: "aw-spin .6s linear infinite" }} />}
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, disabled }) {
  return (
    <div style={{ flex: 1, marginBottom: 8 }}>
      <label className="aw-label">{label}</label>
      <input className="aw-input" style={{ width: "100%", ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ""} disabled={disabled} />
    </div>
  );
}

const S = {
  radio: { fontSize: 11, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", color: "var(--muted)" },
};
